'use client';

import {
	BigNum,
	MarketStatus,
	PERCENTAGE_PRECISION_EXP,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import {
	ENUM_UTILS,
	MarketId,
	PollingSequenceGuard,
	L2WithOracleAndMarketData,
	deserializeL2Response,
	ResultSlotIncrementer,
} from '@velocity-exchange/common';
import { useEffect, useRef } from 'react';
import { NavigationOptionKey } from 'src/components/Navigation/types';
import Env from '../../environmentVariables/EnvironmentVariables';
import useMarketStateStore, {
	MarketListeningSelection,
	MarketState,
} from '../../stores/useMarketStateStore';
import { encodeQueryParams } from '../../utils/explorer';
import useMarketsForDlobToTrack, {
	MarketStatePriority,
} from '../useMarketsForDlobToTrack';
import useIdleLevels, { IdleLevelsState } from '../utils/useIdleLevels';
import useInterval from '../useInterval';
import { useUncrossOrderbook } from './useUncrossOrderbook';
import useDevStore from '../../stores/useDevStore';
import { DriftWindow } from 'src/window/driftWindow';
import useNavigationStore from 'src/stores/useNavigationStore';
import useDriftClientIsReady from '../useDriftClientIsReady';
import useDriftClient from '../useDriftClient';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import { dlog } from 'src/dev';
import useOrderbookDisplayStore, {
	OrderbookSource,
} from 'src/stores/orderbook/useOrderbookDisplayStore';
import { useQueryClient } from '@tanstack/react-query';
import { orderbookQueryKeys } from 'src/hooks/query/orderbook/orderbookQueryKeys';
import { L2OrderbookWithSlot } from 'src/hooks/query/orderbook/types';
import usePricingSourceStore from 'src/stores/pricing/usePricingSourceStore';
import { PRICING_QUERY_KEY } from 'src/hooks/query/pricing/usePricingData';
import { PricingData } from 'src/hooks/query/pricing/types';

const SHALLOW_PRICE_L2_DEPTH = 1;
const DEEP_PRICE_L2_DEPTH = 1;
const SELECTED_MARKET_L2_DEPTH = 100;

export const BASE_TICK_INTERVAL_MS = 1000;

const LIVE_MARKET_BASE_TICK_INTERVAL_MULTIPLIER = 1;
export const BACKGROUND_DEEP_TICK_INTERVAL_MULTIPLIER = 10; // Poll position markets every 10 seconds (fallback only when pricing WS is disconnected)
export const BACKGROUND_SHALLOW_TICK_INTERVAL_MULTIPLIER = 30;

const IDLE_1_BACKGROUND_TICK_INTERVAL_MULTIPLIER = 30;
const IDLE_2_BACKGROUND_TICK_INTERVAL_MULTIPLIER = 60;

const BULK_FETCH_DLOB_L2_URL = `${Env.dlobServerHttpUrl}/batchL2`;
const BULK_FETCH_DLOB_L2_CACHED_URL = `${Env.dlobServerHttpUrl}/batchL2Cache`;

const SPOT_MARKETS_WITH_DLOB_ORACLE = [1, 15]; // SOL, DRIFT

type BulkL2FetchingQueryParams = {
	marketIndex: number;
	marketType: string;
	depth: number;
	includeVamm: boolean;
	includeOracle: boolean;
	includeIndicative: boolean;
};

type BulkL2FetchingParams = {
	markets: (BulkL2FetchingQueryParams & { intervalMultiplier: number })[];
	grouping?: number;
};

const BACKGROUND_L2_POLLING_KEY = Symbol('BACKGROUND_L2_POLLING_KEY');

const resultSlotIncrementer = new ResultSlotIncrementer();

const BULK_DLOB_L2_FETCHER = (params: BulkL2FetchingParams) => {
	const queryParamsMap: {
		[K in keyof BulkL2FetchingQueryParams]: string;
	} & {
		grouping?: string;
	} = {
		marketType: params.markets.map((market) => market.marketType).join(','),
		marketIndex: params.markets.map((market) => market.marketIndex).join(','),
		depth: params.markets.map((market) => market.depth).join(','),
		includeVamm: params.markets.map((market) => market.includeVamm).join(','),
		grouping: params.grouping
			? params.markets.map(() => params.grouping).join(',')
			: undefined,
		includeOracle: params.markets
			.map((market) => market.includeOracle)
			.join(','),
		includeIndicative: params.markets
			.map((market) => market.includeIndicative)
			.join(','),
	};

	const queryParams = encodeQueryParams(queryParamsMap);

	const useCachedEndpoint =
		params.markets.findIndex(
			(market) =>
				market.intervalMultiplier === LIVE_MARKET_BASE_TICK_INTERVAL_MULTIPLIER
		) === -1; // Only use the cached endpoint when we're exclusively fetching "background" markets, which don't have the same liveness requirements.

	return new Promise<L2WithOracleAndMarketData[]>((res, rej) => {
		PollingSequenceGuard.fetch(BACKGROUND_L2_POLLING_KEY, () => {
			return fetch(
				`${
					useCachedEndpoint
						? BULK_FETCH_DLOB_L2_CACHED_URL
						: BULK_FETCH_DLOB_L2_URL
				}?${queryParams}`
			);
		})
			.then(async (r) => {
				const resp = await r.json();

				const resultsArray = resp.l2s as any[];

				const deserializedL2 = resultsArray.map(deserializeL2Response);

				const resultSlot = resp?.l2s?.[0]?.slot;

				const isValidResult = resultSlotIncrementer.handleResult(
					BULK_FETCH_DLOB_L2_URL,
					resultSlot
				);

				if (!isValidResult) {
					res([]); // TODO : BETTER-OLD-SLOT-HANDLING
				}

				res(deserializedL2);
			})
			.catch((e) => {
				console.error('Error fetching dlob', e);
				rej();
			});
	});
};

const getBulkDlobL2ForMarkets = async (props: {
	markets: {
		marketId: MarketId;
		depth: number;
		intervalMultiplier: number;
	}[];
	groupingSize?: number;
	indicativeLiquidityEnabled: boolean;
}): Promise<L2WithOracleAndMarketData[]> => {
	const l2State = await BULK_DLOB_L2_FETCHER({
		markets: props.markets.map((m) => ({
			marketIndex: m.marketId.marketIndex,
			marketType: m.marketId.marketTypeStr,
			depth: m.depth,
			includeVamm: m.marketId.isPerp,
			includeOracle: true,
			includeIndicative: props.indicativeLiquidityEnabled,
			intervalMultiplier: m.intervalMultiplier,
		})),
		grouping: props.groupingSize,
	});
	return l2State;
};

export const fetchBulkMarketL2Data = async (
	markets: {
		marketId: MarketId;
		depth: number;
		intervalMultiplier: number;
	}[],
	indicativeLiquidityEnabled: boolean
): Promise<L2WithOracleAndMarketData[]> => {
	const dlobL2 = await getBulkDlobL2ForMarkets({
		markets,
		indicativeLiquidityEnabled,
	});

	return dlobL2;
};

const PAGES_NOT_REQUIRING_DLOB_SYNCING: NavigationOptionKey[] = [
	'stats',
	'leaderboard',
	'insuranceStaking',
	'borrowLend',
];

type PollingSpec = {
	baseIntervalMultiplier: number;
	dlobDepth: number;
};

const getPollingSpec = (
	type: MarketStatePriority,
	idleLevels: IdleLevelsState,
	navigationSelection: NavigationOptionKey
): PollingSpec => {
	// Get the multiplier to use for background markets (Ones which aren't the currently selected market)
	const tickMultiplier = (() => {
		// Reduce polling when on a page where dlob price doesn't need high resolution updates
		if (PAGES_NOT_REQUIRING_DLOB_SYNCING.includes(navigationSelection)) {
			return IDLE_1_BACKGROUND_TICK_INTERVAL_MULTIPLIER;
		}

		// Reduce polling when page is idle
		if (idleLevels.idle10Min) {
			return IDLE_2_BACKGROUND_TICK_INTERVAL_MULTIPLIER;
		}
		if (idleLevels.idle1Min) {
			return IDLE_1_BACKGROUND_TICK_INTERVAL_MULTIPLIER;
		}

		if (type === MarketStatePriority.SelectedMarket) {
			return LIVE_MARKET_BASE_TICK_INTERVAL_MULTIPLIER;
		}

		if (type === MarketStatePriority.BackgroundDeep) {
			return BACKGROUND_DEEP_TICK_INTERVAL_MULTIPLIER;
		}

		return BACKGROUND_SHALLOW_TICK_INTERVAL_MULTIPLIER;
	})();

	switch (type) {
		case MarketStatePriority.BackgroundDeep: {
			return {
				baseIntervalMultiplier: tickMultiplier,
				dlobDepth: DEEP_PRICE_L2_DEPTH,
			};
		}
		case MarketStatePriority.BackgroundShallow: {
			return {
				baseIntervalMultiplier: tickMultiplier,
				dlobDepth: SHALLOW_PRICE_L2_DEPTH,
			};
		}
		case MarketStatePriority.SelectedMarket: {
			return {
				baseIntervalMultiplier: tickMultiplier,
				dlobDepth: SELECTED_MARKET_L2_DEPTH,
			};
		}
		default: {
			const exhaustiveCheck: never = type;
			throw new Error(`Unhandled case: ${exhaustiveCheck}`);
		}
	}
};

const POLLING_SPEC_TO_USE_FOR_DEEP_MARKETS = MarketStatePriority.BackgroundDeep;
const POLLING_SPEC_TO_USE_FOR_SHALLOW_MARKETS =
	MarketStatePriority.BackgroundShallow;
const POLLING_SPEC_TO_USE_FOR_SELECTED_MARKETS =
	MarketStatePriority.SelectedMarket;

/**
 * A hook which handles keeping market data in sync using a polling stategy against the dlob-server.
 *
 * Key Aspects:
 *
 * # Polling Specs
 * We don't need to fetch all markets with the same depth or frequency. For each different class of detail required we fetch a different amount of depth and at a different frequency. This is encapsulated in the "Polling Spec"
 *
 * # Single Interval Loop
 * The code is structed in a way that all of the different polling specs are handled by a single interval, which discriminates against specs using their "inteval multiplier" and a counter that increments for each fetch loop. The reason for this is because the DLOB-Server has a maximum polling rate (1 per second at the time of this writing) we can exceed by having multiple loops creating seperate fetch requests, even though we'd still be fetching data with the same resolution. By running all of the fetches in a single loop, with varying query params, we can fetch the same amount of data without accidentally exceeding the request rate.
 * @param enabled
 */

const useSyncMarketDataWithDlobServerPolling = () => {
	const queryClient = useQueryClient();

	const shouldBeUsedForOrderbook = useOrderbookDisplayStore(
		(s) => s.orderbookSource === OrderbookSource.POLLING
	);

	const pricingSource = usePricingSourceStore((s) => s.source);
	const shouldPollBackgroundMarkets = pricingSource === 'polling';

	// Use ref to always get the latest value in async callbacks (avoids stale closure)
	const shouldPollBackgroundMarketsRef = useRef(shouldPollBackgroundMarkets);
	useEffect(() => {
		shouldPollBackgroundMarketsRef.current = shouldPollBackgroundMarkets;
	}, [shouldPollBackgroundMarkets]);

	const marketsToTrack = useMarketsForDlobToTrack(
		MarketListeningSelection.DLOB_SERVER_POLLING
	);

	const selectedMarkets =
		marketsToTrack.categories[MarketStatePriority.SelectedMarket];
	const deepPriceMarkets =
		marketsToTrack.categories[MarketStatePriority.BackgroundDeep];
	const shallowPriceMarkets =
		marketsToTrack.categories[MarketStatePriority.BackgroundShallow];

	const enabled = marketsToTrack.totalMarkets > 0;

	const idleLevels = useIdleLevels();
	const driftClient = useDriftClient();
	const driftClientIsReady = useDriftClientIsReady();

	const setDlobStoreState = useMarketStateStore((s) => s.set);
	const setOrderbookStoreState = useOrderbookDisplayStore((s) => s.set);

	const uncrossDisabled_ = useDevStore((s) => s.dlobSettings.disableUncross);
	const uncrossDisabled = useRef(uncrossDisabled_);
	useEffect(() => {
		uncrossDisabled.current = uncrossDisabled_;
	}, [uncrossDisabled_]);

	const uncross = useUncrossOrderbook();

	const currentNavigationSelection = useNavigationStore(
		(s) => s.currentNavigationSelection
	);

	const indicativeLiquidityEnabled = useFeatureFlagStore((s) =>
		s.flagEnabled('ENABLE_INDICATIVE_ORDERBOOK')
	);

	// Track consecutive empty responses for fallback logic
	const consecutiveEmptyResponseCount = useRef(0);
	const MAX_CONSECUTIVE_EMPTY_RESPONSES = 3;

	const getFetchParamsFromPollingSpec = (
		marketId: MarketId,
		pollingSpec: PollingSpec
	) => {
		return {
			marketId,
			depth: pollingSpec.dlobDepth,
			intervalMultiplier: pollingSpec.baseIntervalMultiplier,
		};
	};

	const fetchL2Data = async (
		marketsToFetch: {
			marketId: MarketId;
			pollingSpec: PollingSpec;
		}[]
	) => {
		if (!enabled || marketsToFetch.length === 0) {
			return Promise.resolve([]) as Promise<L2WithOracleAndMarketData[]>;
		}

		const fetchingParams = marketsToFetch.map((spec) =>
			getFetchParamsFromPollingSpec(spec.marketId, spec.pollingSpec)
		);

		// Get current grouping from orderbook store for polling requests
		const groupingSize = shouldBeUsedForOrderbook
			? useOrderbookDisplayStore.getState().groupingSizeSelection
			: undefined;

		const l2Data = await getBulkDlobL2ForMarkets({
			markets: fetchingParams,
			groupingSize,
			indicativeLiquidityEnabled,
		});

		return l2Data;
	};

	const fetchNewMarketData = async (
		marketsToFetch: {
			marketId: MarketId;
			pollingSpec: PollingSpec;
		}[]
	): Promise<
		{
			marketState: MarketState;
			fallbackOrderbookData: { spreadPct: BigNum; spreadQuote: BigNum };
		}[]
	> => {
		const l2State = await fetchL2Data(marketsToFetch);

		if (l2State.length === 0) {
			return []; // TODO : BETTER-OLD-SLOT-HANDLING
		}

		const marketData = l2State.map((l2ForMarket, index) => {
			const marketId = marketsToFetch[index].marketId;
			const orderbookToUse = uncrossDisabled.current
				? l2ForMarket
				: uncross.uncrossL2OrderbookWithOracle(l2ForMarket, marketId);

			// Helper function to create regular oracle data structure
			const createOracleData = (oracleData: any) => ({
				price: BigNum.from(oracleData.price, PRICE_PRECISION_EXP),
				slot: oracleData.slot,
				confidence: oracleData.confidence,
				hasSufficientNumberOfDataPoints:
					oracleData.hasSufficientNumberOfDataPoints,
				twap: BigNum.from(oracleData.twap, PRICE_PRECISION_EXP),
				twapConfidence: oracleData.twapConfidence,
				maxPrice: BigNum.from(oracleData.maxPrice, PRICE_PRECISION_EXP),
			});

			// Helper function to create MM oracle data structure (no twap/twapConfidence/maxPrice, but has isMMOracleActive)
			const createMMOracleData = (mmOracleData: any) => ({
				price: BigNum.from(mmOracleData.price, PRICE_PRECISION_EXP),
				slot: mmOracleData.slot,
				confidence: mmOracleData.confidence,
				hasSufficientNumberOfDataPoints:
					mmOracleData.hasSufficientNumberOfDataPoints,
				isMMOracleActive: mmOracleData.isMMOracleActive,
			});

			// Helper function to create MM oracle fallback (using regular oracle data but adding isMMOracleActive: false)
			const createMMOracleFallback = (oracleData: any) => ({
				price: BigNum.from(oracleData.price, PRICE_PRECISION_EXP),
				slot: oracleData.slot,
				confidence: oracleData.confidence,
				hasSufficientNumberOfDataPoints:
					oracleData.hasSufficientNumberOfDataPoints,
				isMMOracleActive: false,
			});

			// For spot markets (except SOL and DRIFT), fetch oracle data directly from drift client since dlob server no longer publishes it
			let oracleDataToUse = l2ForMarket.oracleData;
			if (
				marketId.isSpot &&
				!SPOT_MARKETS_WITH_DLOB_ORACLE.includes(marketId.marketIndex) &&
				driftClient &&
				driftClientIsReady
			) {
				try {
					const spotOracleData = driftClient.getOracleDataForSpotMarket(
						marketId.marketIndex
					);
					if (spotOracleData) {
						oracleDataToUse = spotOracleData;
					}
				} catch (e) {
					// Fall back to dlob server data if direct fetch fails
					dlog(
						'dlob_polling',
						`Failed to fetch oracle data directly for spot market ${marketId.marketIndex}`,
						e
					);
				}
			}

			return {
				marketState: {
					marketId,
					orderbook: { ...orderbookToUse, slot: l2ForMarket.slot },
					oracle: createOracleData(oracleDataToUse),
					mmOracle: l2ForMarket.mmOracleData
						? createMMOracleData(l2ForMarket.mmOracleData)
						: createMMOracleFallback(oracleDataToUse),
					derivedState: {
						bestAsk: BigNum.from(l2ForMarket.bestAskPrice, PRICE_PRECISION_EXP),
						bestBid: BigNum.from(l2ForMarket.bestBidPrice, PRICE_PRECISION_EXP),
						markPrice: BigNum.from(l2ForMarket.markPrice, PRICE_PRECISION_EXP),
						updateSlot: l2ForMarket.slot,
					},
					marketSlot: l2ForMarket.marketSlot,
				} as MarketState,
				fallbackOrderbookData: {
					spreadPct: BigNum.from(
						l2ForMarket.spreadPct,
						PERCENTAGE_PRECISION_EXP
					),
					spreadQuote: BigNum.from(
						l2ForMarket.spreadQuote,
						QUOTE_PRECISION_EXP
					),
				},
			};
		});

		return marketData;
	};

	const fetchAndUpdateMarketDataForMarkets = async (
		marketsToPoll: {
			marketId: MarketId;
			pollingSpec: PollingSpec;
		}[]
	) => {
		try {
			if (DriftWindow.BREAK_DLOB_SERVER_POLLING) {
				// Dev setting for testing fallback logic
				return;
			}

			const newData = await fetchNewMarketData(marketsToPoll);

			if (newData.length === 0) {
				consecutiveEmptyResponseCount.current++;

				// If this polling sync should be used for orderbook and we've had too many empty responses,
				// fallback to blockchain data source
				if (
					shouldBeUsedForOrderbook &&
					consecutiveEmptyResponseCount.current >=
						MAX_CONSECUTIVE_EMPTY_RESPONSES
				) {
					console.log(
						`🔄 DLOB polling had ${MAX_CONSECUTIVE_EMPTY_RESPONSES} consecutive empty responses, switching orderbook source to BLOCKCHAIN`
					);
					setOrderbookStoreState((s) => {
						s.orderbookSource = OrderbookSource.BLOCKCHAIN;
					});
					consecutiveEmptyResponseCount.current = 0; // Reset count after fallback
				}
				return;
			}

			consecutiveEmptyResponseCount.current = 0; // Reset count on successful fetch

			setDlobStoreState((s) => {
				for (const dataForMarket of newData) {
					s.marketDataState[dataForMarket.marketState.marketId.key] =
						dataForMarket.marketState;
					s.latestUpdateSlot = Math.max(
						s.latestUpdateSlot,
						dataForMarket.marketState.derivedState.updateSlot,
						dataForMarket.marketState.marketSlot ?? 0
					);
				}
			});

			// Update React Query cache with pricing data for all polled markets.
			// Only write when polling is the active pricing source to avoid overwriting fresher WS data.
			// We use a ref here because this code runs after an async operation (fetchNewMarketData).
			// During that await, the pricing source could switch from 'polling' to 'websocket'.
			// If we used the closure-captured value, we'd still see the stale 'polling' state
			// and incorrectly overwrite fresher WebSocket data with older polling data.
			if (shouldPollBackgroundMarketsRef.current) {
				// Clone the map upfront to avoid mutating React Query cache directly
				const currentMap = new Map(
					queryClient.getQueryData<Map<string, PricingData>>(
						PRICING_QUERY_KEY
					) ?? new Map<string, PricingData>()
				);

				for (const dataForMarket of newData) {
					const marketId = dataForMarket.marketState.marketId;
					const derivedState = dataForMarket.marketState.derivedState;
					const oracle = dataForMarket.marketState.oracle;

					currentMap.set(marketId.key, {
						symbol: marketId.key,
						marketIndex: marketId.marketIndex,
						marketType: marketId.marketType,
						oraclePrice: oracle?.price?.toNum() ?? 0,
						markPrice: derivedState?.markPrice?.toNum() ?? 0,
						lastPrice: derivedState?.markPrice?.toNum() ?? 0,
						updateTimestamp: Date.now(),
					});
				}

				queryClient.setQueryData(PRICING_QUERY_KEY, currentMap);
			}

			if (shouldBeUsedForOrderbook) {
				const currentMarketId = useOrderbookDisplayStore.getState().marketId;
				const selectedMarketNewData = newData.find((m) =>
					currentMarketId
						? m.marketState.marketId.equals(currentMarketId as MarketId)
						: false
				);

				if (selectedMarketNewData) {
					const marketKey = selectedMarketNewData.marketState.marketId.key;
					const l2OrderbookWithSlot: L2OrderbookWithSlot = {
						...selectedMarketNewData.marketState.orderbook,
						slot: selectedMarketNewData.marketState.orderbook.slot ?? 0,
					};

					queryClient.setQueryData(
						orderbookQueryKeys.l2Orderbook(marketKey),
						l2OrderbookWithSlot
					);
					queryClient.setQueryData(
						orderbookQueryKeys.marketPriceState(marketKey),
						{
							bestAsk: selectedMarketNewData.marketState.derivedState.bestAsk,
							bestBid: selectedMarketNewData.marketState.derivedState.bestBid,
							markPrice:
								selectedMarketNewData.marketState.derivedState.markPrice,
							spreadPct: selectedMarketNewData.fallbackOrderbookData.spreadPct,
							spreadQuote:
								selectedMarketNewData.fallbackOrderbookData.spreadQuote,
							slot: selectedMarketNewData.marketState.marketSlot,
						}
					);

					setOrderbookStoreState((s) => {
						s.loading = false;
					});
				}
			}
		} catch (e) {
			console.error('DLOB polling failed:', e);
			consecutiveEmptyResponseCount.current = 0; // Reset count on error

			// If this polling sync should be used for orderbook and it's failing,
			// fallback to blockchain data source
			if (shouldBeUsedForOrderbook) {
				console.log(
					'🔄 DLOB polling failed, switching orderbook source to BLOCKCHAIN'
				);
				setOrderbookStoreState((s) => {
					s.orderbookSource = OrderbookSource.BLOCKCHAIN;
				});
			}
		}
	};

	/**
	 * This is a lookup which holds the polling spec for each individual market.
	 */
	const marketPollingSpecLookup = useRef<
		Map<string, { marketId: MarketId; pollingSpec: PollingSpec }>
	>(new Map());
	const tickCounter = useRef(0);

	/**
	 * This handler ensures the polling spec lookup remains correct as required
	 * @param markets
	 * @param pollingType
	 * @returns
	 */
	const marketPollingSpecSyncHandler = (
		markets: MarketId[],
		pollingType: MarketStatePriority
	) => {
		if (!enabled) return;

		// # Selected Market Handler
		const pollingProps = getPollingSpec(
			pollingType,
			idleLevels,
			currentNavigationSelection ?? 'perpetuals'
		);

		for (const marketId of markets) {
			marketPollingSpecLookup.current.set(marketId.key, {
				marketId: marketId,
				pollingSpec: pollingProps,
			});
		}
	};

	const marketPollingSpecRemovalHandler = (markets: MarketId[]) => {
		if (!enabled) return;

		for (const marketId of markets) {
			marketPollingSpecLookup.current.delete(marketId.key);
		}
	};

	/**
	 * Handler to actually fetch the l2 data on each interval tick.
	 *
	 * We filter markets to poll in each tick based on their multiplier .. when the modulus of the multiplier and the interval tick count is 0 then we know that we should poll for this market.
	 *
	 * The reason we do it like this is so that we can keep a single interval running to cover all different polling specs, instead of needing to set up one for each different type. Reasoning for this is explained in the large comment at the top of the file.
	 */
	const ticksStarted = useRef(false);
	const intervalTickHandler = () => {
		if (marketPollingSpecLookup.current.size === 0 && !ticksStarted.current) {
			// Skip ticks until the initial markets have been placed into the polling spec lookup
			return;
		}

		if (!driftClientIsReady) {
			return;
		}

		if (!ticksStarted.current) {
			ticksStarted.current = true;
		}

		dlog('market_subscriptions', 'intervalTickHandler', {
			ticksStarted: ticksStarted.current,
			marketPollingSpecLookup: marketPollingSpecLookup.current.size,
		});

		const marketsToPoll = Array.from(marketPollingSpecLookup.current.values())
			.filter((pollingInfo) => {
				return (
					tickCounter.current %
						pollingInfo.pollingSpec.baseIntervalMultiplier ===
					0
				);
			})
			.filter((market) => {
				const marketAccount = market.marketId.isSpot
					? driftClient?.getSpotMarketAccount(market.marketId.marketIndex)
					: driftClient?.getPerpMarketAccount(market.marketId.marketIndex);
				if (!marketAccount) return false;
				const status = marketAccount.status;
				return !ENUM_UTILS.match(status, MarketStatus.DELISTED);
			});

		fetchAndUpdateMarketDataForMarkets(marketsToPoll);

		tickCounter.current++;
	};

	// Reset tick counter and consecutive empty response count when disabled
	useEffect(() => {
		if (!enabled) {
			tickCounter.current = 0;
			consecutiveEmptyResponseCount.current = 0;
		}
	}, [enabled]);

	// Set up selected market polling interval
	useEffect(() => {
		if (!selectedMarkets) return;
		if (!shouldBeUsedForOrderbook) return;

		marketPollingSpecSyncHandler(
			selectedMarkets,
			POLLING_SPEC_TO_USE_FOR_SELECTED_MARKETS
		);

		return () => {
			marketPollingSpecRemovalHandler(selectedMarkets);
		};
	}, [selectedMarkets, enabled, shouldBeUsedForOrderbook]);

	// Set up deep market polling interval
	// Only poll background markets when pricing WebSocket is not connected
	useEffect(() => {
		if (!deepPriceMarkets) return;
		if (!shouldPollBackgroundMarkets) {
			marketPollingSpecRemovalHandler(deepPriceMarkets);
			return;
		}
		marketPollingSpecSyncHandler(
			deepPriceMarkets,
			POLLING_SPEC_TO_USE_FOR_DEEP_MARKETS
		);

		return () => {
			marketPollingSpecRemovalHandler(deepPriceMarkets);
		};
	}, [deepPriceMarkets, enabled, shouldPollBackgroundMarkets]);

	// Set up shallow market polling interval
	// Only poll background markets when pricing WebSocket is not connected
	useEffect(() => {
		if (!shallowPriceMarkets) return;
		if (!shouldPollBackgroundMarkets) {
			marketPollingSpecRemovalHandler(shallowPriceMarkets);
			return;
		}

		marketPollingSpecSyncHandler(
			shallowPriceMarkets,
			POLLING_SPEC_TO_USE_FOR_SHALLOW_MARKETS
		);

		return () => {
			marketPollingSpecRemovalHandler(shallowPriceMarkets);
		};
	}, [shallowPriceMarkets, enabled, shouldPollBackgroundMarkets]);

	// Set up the tick interval handler
	useInterval(intervalTickHandler, BASE_TICK_INTERVAL_MS);
};

export default useSyncMarketDataWithDlobServerPolling;
